import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchBrands } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function SearchBox({ large = false }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setHits([]); return; }
    const h = setTimeout(async () => {
      try {
        const d = await searchBrands(q);
        setHits(d.hits || []);
        setOpen(true);
      } catch (e) { console.debug("search failed", e); }
    }, 180);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (hits[0]) { nav(`/brands/${hits[0].slug}`); return; }
    if (q.trim()) nav(`/not-indexed?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="relative" ref={boxRef}>
      <form onSubmit={submit} className="flex items-center border bg-white" style={{ borderColor: "var(--bp-border)" }}>
        <Search size={18} className="ml-4" style={{ color: "var(--bp-text-muted)" }} />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => hits.length && setOpen(true)}
          placeholder={t.search_placeholder}
          className={`flex-1 bg-transparent outline-none px-3 ${large ? "py-4 text-lg" : "py-2.5"}`}
          style={{ color: "var(--bp-text)" }}
        />
        <button
          type="submit"
          data-testid="search-submit"
          className={`bg-black text-white px-6 ${large ? "py-4" : "py-2.5"} text-sm uppercase tracking-widest`}
        >
          Search
        </button>
      </form>
      {open && q.length >= 2 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border shadow-md max-h-80 overflow-auto" style={{ borderColor: "var(--bp-border)" }} data-testid="search-dropdown">
          {hits.length === 0 && (
            <Link to={`/not-indexed?q=${encodeURIComponent(q)}`} onClick={() => setOpen(false)} className="block px-4 py-3 text-sm hover:bg-neutral-50">
              <span className="italic" style={{ color: "var(--bp-text-muted)" }}>
                No brand matches &ldquo;{q}&rdquo; — request its addition →
              </span>
            </Link>
          )}
          {hits.map((h) => (
            <Link
              key={h.slug}
              to={`/brands/${h.slug}`}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between px-4 py-3 text-sm hover:bg-neutral-50 border-b last:border-b-0"
              style={{ borderColor: "var(--bp-border)" }}
              data-testid={`search-hit-${h.slug}`}
            >
              <span className="font-serif text-lg">{h.display_name}</span>
              <span className="text-xs font-mono uppercase" style={{ color: "var(--bp-text-muted)" }}>
                {h.sector_code} · {h.country}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
